import {StateController} from "@lit-app/state/src/state-controller";
import {html, nothing, PropertyValues, TemplateResult} from "lit";
import {customElement} from "lit/decorators/custom-element.js";
import {property} from "lit/decorators/property.js";
import {query} from "lit/decorators/query.js";
import {state} from "lit/decorators/state.js";
import {classMap} from "lit/directives/class-map.js";
import {repeat} from "lit/directives/repeat.js";
import {get} from "lit-translate";
import {DateTime} from "luxon";
import {
    animationFrameScheduler,
    auditTime,
    catchError,
    debounce,
    EMPTY,
    filter,
    from,
    fromEvent,
    map,
    merge,
    race,
    Subject,
    switchMap,
    takeUntil,
    timer
} from "rxjs";
import {container} from "tsyringe";
import {appState} from "../app.state";
import {calculateMsUntilMidnight} from "./abstract-o2d-timeline.view";
import {AbstractLitElement} from "../components/abstract-lit.element";
import {RxjsLifecycleController} from "../components/_reactive-controllers/rxjs-lifecycle.controller";
import {AuditLogRepository} from "../domain/repositories/audit-log.repository";
import {IssueRepository} from "../domain/repositories/issue.repository";
import {Issue} from "../domain/entities/issue.entity";
import {FilterHelper} from "../helpers/filter.helper";
import {buildTasklistIssueTypeItemFilter} from "../helpers/issue-type-filter-scope.helper";
import {AnchorCandidate, TasklistScrollAnchorHelper} from "../helpers/tasklist-scroll-anchor.helper";
import {
    TASKLIST_KEY_TODAY_DIVIDER,
    TasklistEntries,
    TasklistEntriesHelper,
    TasklistEntry
} from "../helpers/tasklist-entries.helper";
import {TasklistJobQueue} from "../helpers/tasklist-job-queue";
import {TasklistSaveGate} from "../helpers/tasklist-save-gate";
import {TasklistScrollKeeper} from "../helpers/tasklist-scroll-keeper";
import {
    TASKLIST_BLOCK_MAX,
    TASKLIST_EXTEND_STEP,
    TASKLIST_EXTEND_STEP_DRAG,
    TasklistBlockBounds,
    TasklistBlockSide,
    TasklistWindowHelper
} from "../helpers/tasklist-window.helper";
import {ToolbarHelper} from "../helpers/toolbar.helper";
import {MessageBroker} from "../message-broker/message-broker";
import {EntityCreatedPayload} from "../message-broker/payloads/entity-created.payload";
import {TaskListQueryItem} from "../types/task-list-query.item.type";
import {TimelineToolbarItem} from "../types/timeline-toolbar-item.interface";
import {issueFiltersToolbarItem} from "../toolbar-items/o2d-issue-filters.toolbar-item";
import {tasklistResetToTodayToolbarItem} from "../toolbar-items/o2d-tasklist-reset-to-today.toolbar-item";
import {tasklistToggleViewToolbarItem} from "../toolbar-items/o2d-tasklist-toggle-view.toolbar-item";
import {addEntityMenuToolbarItem} from "../toolbar-items/o2d-add-entity-menu.toolbar-item";

/** Keys that move the list by keyboard; with them the user scrolls although no wheel or pointer is used. */
const NAVIGATION_KEYS = new Set(['ArrowUp', 'ArrowDown', 'PageUp', 'PageDown', 'Home', 'End', ' ']);

/**
 * The tasklist (`/tasklist`, `/absence-list` via `mode`): every issue as one row, sorted by date.
 *
 * RENDERING — the block model. Only a contiguous block of real cards around a null point is in the
 * DOM, and it grows at its edges as the user scrolls (TasklistWindowHelper). This replaced
 * `@lit-labs/virtualizer`, which estimated the position of every unrendered card as index × average
 * height; with ~4 500 unmeasured entries above the position that estimate was off by about
 * ±200 000 px, and the virtualizer's own correction made the list jump (measured on a copy of the
 * srv-999 data, 2026-09-30). Every position here comes from rendered, measured cards; the scrollbar
 * shows the block, not the whole list.
 *
 * WHO WRITES `scrollTop`. Exactly one job at a time (TasklistJobQueue) changes the block or jumps;
 * between jobs only TasklistScrollKeeper compensates content changes above the view. The browser's
 * scroll anchoring is off for the list.
 *
 * WHEN THE ANCHOR IS SAVED. The topmost card at least half visible is saved per mode — but after a
 * jump only once the user has really moved the list (TasklistSaveGate).
 */
@customElement(`ha-tasklist-view`)
export class HaTasklistView extends AbstractLitElement {

    /** Edge markers count as near when within this distance (px) of the view; above the cards' 800 px load band. */
    private static readonly MARKER_DISTANCE_PX = 1000;

    private static readonly ANCHOR_PERSIST_DEBOUNCE_MS = 150;

    /** Upper bound for the release after a jump: `requestAnimationFrame` pauses in background tabs. */
    private static readonly RELEASE_TIMEOUT_MS = 1000;

    /**
     * Distinguishes the two tasklist routes, which render this same tag with a different `mode`
     * attribute rather than duplicating the fetch/sort/render logic in a second component.
     */
    @property({type: String})
    public mode: 'tasks' | 'absences' = 'tasks';

    private _firstIssueUidInFuture: string | null = null;

    private _issueRepository: IssueRepository = container.resolve(IssueRepository);

    private _messageBroker: MessageBroker = container.resolve(MessageBroker);

    /** Reconnect-safe teardown signal (ADR-007); NOT the inherited `_disconnected$`, which stays completed after the first disconnect. */
    private readonly _rxjs = new RxjsLifecycleController(this);

    /** All rows of the list (dividers and the no-results notice included), in display order. */
    @state()
    private _entries: TasklistEntry[] | null = null;

    /** The rendered part of `_entries`. */
    @state()
    private _block: TasklistBlockBounds | null = null;

    @state()
    private _view: 'list-view' | 'column-view' = 'list-view';

    // No initializer: @query defines a getter-only accessor on the prototype.
    @query('div.ha-tasklist__issue-container')
    private _listContainer!: HTMLDivElement | null;

    private _todayKey: string = TASKLIST_KEY_TODAY_DIVIDER;

    private _isEmpty: boolean = false;

    private _queue: TasklistJobQueue | null = null;

    private _keeper: TasklistScrollKeeper | null = null;

    private _markerObserver: IntersectionObserver | null = null;

    private readonly _saveGate = new TasklistSaveGate();

    /** Bumped on every connect; a job that finds a different value after an `await` stops. */
    private _connection = 0;

    /** Bumped when a jump is enqueued; an anchor value scanned before it is never saved. */
    private _jumpGeneration = 0;

    /** True from enqueueing a jump until it has released; the anchor scan pauses meanwhile. */
    private _jumpPending = false;

    /** True while an "ensure edges" job waits in the queue. */
    private _ensurePending = false;

    /** True while the scrollbar is dragged: extensions then add TASKLIST_EXTEND_STEP_DRAG entries. */
    private _draggingScrollbar = false;

    /** Number of the latest reload request; an older response is skipped. */
    private _reloadNumber = 0;

    private _reload$ = new Subject<number>();

    private _scan$ = new Subject<{ uid: string, generation: number }>();

    private _flush$ = new Subject<void>();

    private _unsubscribeAppState: (() => void) | null = null;

    private _containerStreamsBound = false;

    private _toolbarItems: TimelineToolbarItem[] = [
        issueFiltersToolbarItem,
        tasklistResetToTodayToolbarItem,
        tasklistToggleViewToolbarItem,
        addEntityMenuToolbarItem,
    ];

    public constructor() {
        super();
        new StateController(this, appState);
    }

    public connectedCallback() {
        super.connectedCallback();
        this._connection++;
        this._queue = new TasklistJobQueue();
        this._reload$ = new Subject<number>();
        this._scan$ = new Subject<{ uid: string, generation: number }>();
        this._flush$ = new Subject<void>();
        this._ensurePending = false;
        this._jumpPending = false;
        this._containerStreamsBound = false;

        // `tasklistView` is persisted and pushed into `_view` reactively only on a change — take the
        // current value too, so a page load that starts in column-view does not render list-view.
        this._view = appState.tasklistView;

        this._unsubscribeAppState = appState.subscribe(key => {
            if (key === `filters`) {
                this._requestReload();
            }
            if (key === 'tasklistView' && appState.tasklistView !== this._view) {
                this._toggleView(appState.tasklistView);
            }
        });

        // Built here, before the first load is requested below — a request sent before anyone
        // subscribed would be lost and nothing would load on the first mount.
        this._bindReload();
        this._bindAnchorSave();

        // Reloads the list when an issue is created elsewhere. `bufferTime: 150` bundles a burst of
        // creates into one reload.
        this._messageBroker.registerListener<EntityCreatedPayload<Issue>>(this, {
            messageType: `entity.created`,
            bufferTime: 150,
            filterFn: message => message.payload.prototype === Issue,
            method: () => this._requestReload(),
            disconnect: this._rxjs.disconnected$
        });

        this._startMidnightTimer();

        // Reconnect of an already rendered instance (navigated away and back): firstUpdated() runs
        // only once per instance, so the container streams are bound here for every later connect.
        if (this.hasUpdated) {
            this._bindContainerStreams();
        }

        this._requestReload();
    }

    public disconnectedCallback() {
        this._queue?.close();
        this._keeper?.dispose();
        this._keeper = null;
        this._markerObserver?.disconnect();
        this._markerObserver = null;
        this._unsubscribeAppState?.();
        this._unsubscribeAppState = null;
        this._reload$.complete();
        this._scan$.complete();
        this._flush$.complete();
        super.disconnectedCallback();
    }

    protected firstUpdated(_changedProperties: PropertyValues): void {
        this._bindContainerStreams();
    }

    protected shouldUpdate(_changedProperties: PropertyValues): boolean {
        return Array.isArray(this._entries) && super.shouldUpdate(_changedProperties);
    }

    /**
     * Scrolls to the "today" line (or, without entries from today on, to the last entry) and stores
     * "today" (null) as the anchor. Called from the toolbar.
     */
    public scrollToToday(): void {
        if (!this._entries) {
            return;
        }

        this._enqueueJump(async () => {
            await this._jumpTo(this._todayKey);
            appState.setTaskListScrollAnchor(this.mode, null);
        });
    }

    // ---------------------------------------------------------------- loading

    /**
     * Reloads the list at the next local midnight, so the server-computed "today" line reflects the
     * new day when the view is left open across a day change. Reschedules itself.
     */
    private _startMidnightTimer(): void {
        timer(calculateMsUntilMidnight(DateTime.now()))
            .pipe(takeUntil(this._rxjs.disconnected$))
            .subscribe(() => {
                this._requestReload();
                this._startMidnightTimer();
            });
    }

    private _requestReload(): void {
        this._reload$.next(++this._reloadNumber);
    }

    /**
     * Fetches on every reload request. `switchMap` only covers the REQUEST: an outdated request is
     * dropped before its response touches anything. A response that survives becomes a queue job that
     * is never cancelled; it skips itself if a newer request has been made meanwhile.
     */
    private _bindReload(): void {
        this._reload$.pipe(
            switchMap(number => from(this._fetchEntries()).pipe(
                catchError(error => {
                    console.error('tasklist reload failed', error);
                    return EMPTY;
                }),
                // Pair the response with the number of the request it answers.
                map(result => ({number, result})),
            )),
            takeUntil(this._rxjs.disconnected$),
        ).subscribe(({number, result}) => {
            void this._queue?.enqueue(() => this._applyLoad(number, result));
        });
    }

    /** Loads the uid list including deleted issues (needed to locate a deleted stored anchor). */
    private async _fetchEntries(): Promise<{ raw: TaskListQueryItem[], built: TasklistEntries }> {
        const effectiveSearchTerm = appState.filters.searchText || null;
        const {searchText, ...filtersWithoutSearch} = appState.filters;
        const raw = await this._issueRepository.findAllUuidsByStatus(
            FilterHelper.asQueryParams(filtersWithoutSearch),
            effectiveSearchTerm,
            this.mode,
            true
        );

        return {raw, built: await this._buildEntries(raw)};
    }

    /** Builds the rendered rows; only an empty result asks which "no results" message applies. */
    private async _buildEntries(raw: TaskListQueryItem[]): Promise<TasklistEntries> {
        const hasLive = raw.some(item => item.isDeleted !== true);
        return TasklistEntriesHelper.build(raw, hasLive ? '' : await this._resolveNoResultsMessage());
    }

    /**
     * Applies a loaded list. On the first load the block is built around the null point (the stored
     * anchor; a deleted anchor gives the next following live issue). On later loads — filter change,
     * a created issue, midnight — the block is kept by its keys and the view stays where it is.
     */
    private async _applyLoad(number: number, result: { raw: TaskListQueryItem[], built: TasklistEntries }): Promise<void> {
        if (number !== this._reloadNumber) {
            return;
        }

        const connection = this._connection;
        const oldKeys = this._keys();
        const oldBlock = this._block;
        const firstLoad = oldBlock === null || this._entries === null;

        this._entries = result.built.entries;
        this._todayKey = result.built.todayKey;
        this._isEmpty = result.built.isEmpty;

        if (this._isEmpty) {
            const keys = this._keys();
            this._block = {firstKey: keys[0], lastKey: keys[keys.length - 1]};
            await this.updateComplete;
            return;
        }

        if (firstLoad) {
            const nullUid = TasklistWindowHelper.resolveNullPoint(result.raw, appState.taskListScrollAnchor[this.mode]);
            this._markJumpPending();
            await this._jumpTo(nullUid ?? this._todayKey);
            return;
        }

        const keys = this._keys();
        const kept = TasklistWindowHelper.remapAfterReload(oldKeys, keys, oldBlock!);
        if (kept === null) {
            this._markJumpPending();
            await this._jumpTo(this._todayKey);
            return;
        }

        this._block = TasklistWindowHelper.cap(keys, kept, 'top', TASKLIST_BLOCK_MAX, TASKLIST_KEY_TODAY_DIVIDER);
        await this.updateComplete;
        if (connection !== this._connection) {
            return;
        }
        this._keeper?.compensate();
        this._scheduleEnsureEdges();
    }

    // ---------------------------------------------------------------- jumps

    /** Marks a jump as pending: the anchor scan pauses and values scanned before it are discarded. */
    private _markJumpPending(): void {
        this._jumpGeneration++;
        this._jumpPending = true;
    }

    private _enqueueJump(job: () => Promise<void>): void {
        this._markJumpPending();
        void this._queue?.enqueue(job);
    }

    /**
     * Switches list ↔ column view. A pending anchor save of the list view is written FIRST — the
     * other order would discard it — then the block is rebuilt around the anchor in the new layout.
     */
    private _toggleView(view: 'list-view' | 'column-view'): void {
        this._flush$.next();
        this._enqueueJump(async () => {
            this._view = view;
            await this.updateComplete;
            const anchor = appState.taskListScrollAnchor[this.mode];
            const keys = this._keys();
            await this._jumpTo(anchor !== null && keys.includes(anchor) ? anchor : this._todayKey);
        });
    }

    /**
     * Builds the block around `nullKey` and puts that row at the top — inside a queue job. Compensation
     * is suspended meanwhile: the lead-in is filled (measured, not estimated) BEFORE the one absolute
     * write of `scrollTop`. Afterwards saving waits for real user movement (TasklistSaveGate).
     */
    private async _jumpTo(nullKey: string): Promise<void> {
        const connection = this._connection;
        this._keeper?.suspend();
        try {
            const keys = this._keys();
            this._block = TasklistWindowHelper.buildAround(keys, nullKey, TASKLIST_EXTEND_STEP, 2 * TASKLIST_EXTEND_STEP);
            await this.updateComplete;
            if (connection !== this._connection) {
                return;
            }
            // On the first load the keeper only comes into being with the first render (firstUpdated).
            this._keeper?.suspend();

            await this._fillLeadIn(nullKey, connection);
            if (connection !== this._connection) {
                return;
            }

            const listContainer = this._listContainer;
            const nullRow = this._rowByKey(nullKey);
            if (listContainer && nullRow) {
                // Clamped by the browser when the null point sits in the last screen.
                listContainer.scrollTop = nullRow.offsetTop;
            }

            await Promise.race([
                new Promise<void>(resolve => requestAnimationFrame(() => resolve())),
                new Promise<void>(resolve => setTimeout(resolve, HaTasklistView.RELEASE_TIMEOUT_MS)),
            ]);
        } finally {
            // Keep the null point itself in place while the cards around it load, until the user scrolls.
            this._keeper?.pin(this._rowByKey(nullKey));
            this._keeper?.resume();
            this._saveGate.jumped(this._topmostUid());
            this._jumpPending = false;
        }

        this._scheduleEnsureEdges();
    }

    /**
     * Extends the block until the null point has at least max(view height, marker distance) of real
     * cards above it and view height + marker distance below — measured on what is rendered.
     */
    private async _fillLeadIn(nullKey: string, connection: number): Promise<void> {
        const listContainer = this._listContainer;
        if (!listContainer) {
            return;
        }

        const leadAbove = Math.max(listContainer.clientHeight, HaTasklistView.MARKER_DISTANCE_PX);
        const leadBelow = listContainer.clientHeight + HaTasklistView.MARKER_DISTANCE_PX;
        for (let round = 0; round < TASKLIST_BLOCK_MAX; round++) {
            const nullRow = this._rowByKey(nullKey);
            if (!nullRow || !this._block) {
                return;
            }

            const keys = this._keys();
            let next = this._block;
            if (nullRow.offsetTop < leadAbove && TasklistWindowHelper.canExtend(keys, next, 'top')) {
                next = TasklistWindowHelper.extend(keys, next, 'top', TASKLIST_EXTEND_STEP);
            }
            if (listContainer.scrollHeight - nullRow.offsetTop < leadBelow && TasklistWindowHelper.canExtend(keys, next, 'bottom')) {
                next = TasklistWindowHelper.extend(keys, next, 'bottom', TASKLIST_EXTEND_STEP);
            }
            const range = TasklistWindowHelper.resolve(keys, next);
            if (next === this._block || range === null || range.end - range.start + 1 > TASKLIST_BLOCK_MAX) {
                return;
            }

            this._block = next;
            await this.updateComplete;
            if (connection !== this._connection) {
                return;
            }
        }
    }

    // ---------------------------------------------------------------- edges

    /**
     * Whether `side` needs an extension now: its marker is within the marker distance of the view
     * (read synchronously from the geometry, not from the last observer message, which is stale right
     * after a DOM change) AND the block does not already reach the list start/end on that side.
     */
    private _needsExtension(side: TasklistBlockSide): boolean {
        const listContainer = this._listContainer;
        if (!listContainer || !this._block || this._isEmpty) {
            return false;
        }

        const marker = listContainer.querySelector(side === 'top' ? '.ha-tasklist__edge--top' : '.ha-tasklist__edge--bottom');
        if (!marker || !TasklistWindowHelper.canExtend(this._keys(), this._block, side)) {
            return false;
        }

        const view = listContainer.getBoundingClientRect();
        const rect = marker.getBoundingClientRect();

        return side === 'top'
            ? rect.bottom >= view.top - HaTasklistView.MARKER_DISTANCE_PX
            : rect.top <= view.bottom + HaTasklistView.MARKER_DISTANCE_PX;
    }

    /** Enqueues "ensure edges" when an edge needs it and none is waiting or a jump is pending. */
    private _scheduleEnsureEdges(): void {
        if (this._ensurePending || this._jumpPending) {
            return;
        }
        if (!this._needsExtension('top') && !this._needsExtension('bottom')) {
            return;
        }

        this._ensurePending = true;
        let changed = false;
        void this._queue?.enqueue(async () => {
            this._ensurePending = false;
            changed = await this._ensureEdges();
        }).finally(() => {
            this._ensurePending = false;
            // Only after a change: re-checking after a job that changed nothing could requeue without end.
            if (changed) {
                this._scheduleEnsureEdges();
            }
        });
    }

    /**
     * Extends (and trims the far side) while an edge needs it; ends at the list boundaries. Returns
     * whether the block changed.
     */
    private async _ensureEdges(): Promise<boolean> {
        const connection = this._connection;
        let changed = false;
        for (let round = 0; round < TASKLIST_BLOCK_MAX; round++) {
            const side: TasklistBlockSide | null = this._needsExtension('top') ? 'top' : (this._needsExtension('bottom') ? 'bottom' : null);
            if (side === null || !this._block) {
                return changed;
            }

            const keys = this._keys();
            const step = this._draggingScrollbar ? TASKLIST_EXTEND_STEP_DRAG : TASKLIST_EXTEND_STEP;
            const grown = TasklistWindowHelper.extend(keys, this._block, side, step);
            const next = TasklistWindowHelper.cap(keys, grown, side, TASKLIST_BLOCK_MAX, TASKLIST_KEY_TODAY_DIVIDER);
            if (next.firstKey === this._block.firstKey && next.lastKey === this._block.lastKey) {
                return changed;
            }

            this._block = next;
            changed = true;
            await this.updateComplete;
            if (connection !== this._connection) {
                return changed;
            }
            // Same pass as the DOM change: a frame in between would let a drag or fling movement of
            // that frame enter the difference and be undone.
            this._keeper?.compensate();
        }

        return changed;
    }

    // ---------------------------------------------------------------- streams on the container

    /** Binds everything that needs the rendered container. Once per connection. */
    private _bindContainerStreams(): void {
        const listContainer = this._listContainer;
        if (!listContainer || this._containerStreamsBound) {
            return;
        }
        this._containerStreamsBound = true;
        const until$ = this._rxjs.disconnected$;

        this._keeper = new TasklistScrollKeeper(listContainer, element => !element.classList.contains('ha-tasklist__edge'));
        this._keeper.start(until$);

        this._markerObserver = new IntersectionObserver(entries => {
            if (entries.some(entry => entry.isIntersecting)) {
                this._scheduleEnsureEdges();
            }
        }, {root: listContainer, rootMargin: `${HaTasklistView.MARKER_DISTANCE_PX}px 0px`});
        listContainer.querySelectorAll('.ha-tasklist__edge').forEach(marker => this._markerObserver!.observe(marker));

        fromEvent(listContainer, 'scroll', {passive: true}).pipe(
            auditTime(0, animationFrameScheduler),
            takeUntil(until$),
        ).subscribe(() => {
            this._scanAnchor();
            this._scheduleEnsureEdges();
        });

        // Real user input — the only thing that lets the anchor be saved again after a jump. Listened
        // to passively; nothing is prevented.
        merge(
            fromEvent(listContainer, 'wheel', {passive: true}),
            fromEvent(listContainer, 'touchstart', {passive: true}),
            fromEvent<PointerEvent>(listContainer, 'pointerdown'),
            fromEvent<KeyboardEvent>(document, 'keydown').pipe(filter(event => this._isNavigationKey(event))),
        ).pipe(takeUntil(until$)).subscribe(() => {
            this._saveGate.userInput();
            this._keeper?.pin(null);
        });

        // Dragging the scrollbar: its press lands right of the content box (`offsetX >= clientWidth`).
        // Overlay scrollbars (macOS, touch) have no width and are not detected; the step stays small there.
        fromEvent<PointerEvent>(listContainer, 'pointerdown').pipe(takeUntil(until$)).subscribe(event => {
            this._draggingScrollbar = event.offsetX >= listContainer.clientWidth;
        });
        merge(
            fromEvent(document, 'pointerup'),
            fromEvent(document, 'pointercancel'),
            fromEvent(listContainer, 'lostpointercapture'),
        ).pipe(takeUntil(until$)).subscribe(() => {
            this._draggingScrollbar = false;
        });
    }

    private _isNavigationKey(event: KeyboardEvent): boolean {
        const target = event.target as HTMLElement | null;
        const editing = !!target && (target.isContentEditable || ['INPUT', 'TEXTAREA', 'SELECT'].includes(target.tagName));
        return !editing && NAVIGATION_KEYS.has(event.key);
    }

    // ---------------------------------------------------------------- anchor

    /**
     * Saves the scanned anchor after a 150 ms pause in scrolling. A value is dropped when a jump has
     * been enqueued since it was scanned (its generation is outdated) — a plain "not while restoring"
     * filter would let a value queued just before the jump through after a release that comes sooner
     * than 150 ms, which is how "today" got overwritten. `flush$` writes a pending value at once.
     */
    private _bindAnchorSave(): void {
        this._scan$.pipe(
            debounce(() => race(timer(HaTasklistView.ANCHOR_PERSIST_DEBOUNCE_MS), this._flush$)),
            filter(scan => scan.generation === this._jumpGeneration && !this._jumpPending),
            filter(scan => scan.uid !== appState.taskListScrollAnchor[this.mode]),
            takeUntil(this._rxjs.disconnected$),
        ).subscribe(scan => appState.setTaskListScrollAnchor(this.mode, scan.uid));
    }

    /**
     * Finds the topmost card at least half visible and queues it for saving. Not in column view: its
     * grid layout is not top-to-bottom in DOM order. Not while a jump is pending, and after a jump only
     * once the user has moved the list (TasklistSaveGate).
     */
    private _scanAnchor(): void {
        if (this._jumpPending || this._view !== 'list-view') {
            return;
        }

        const uid = this._topmostUid();
        if (uid === null || !this._saveGate.allows(uid)) {
            return;
        }

        this._scan$.next({uid, generation: this._jumpGeneration});
    }

    /** The uid of the topmost card that is at least half visible, or null. */
    private _topmostUid(): string | null {
        const listContainer = this._listContainer;
        if (!listContainer) {
            return null;
        }

        return TasklistScrollAnchorHelper.findTopmostAtLeastHalfVisible(
            this._cardsFromView(listContainer),
            listContainer.getBoundingClientRect()
        );
    }

    /**
     * Cards from the first one reaching into the view onwards, lazily. Found by binary search on
     * `offsetTop`, so a 500-card block is not measured card by card on every scroll frame.
     */
    private* _cardsFromView(listContainer: HTMLElement): Generator<AnchorCandidate> {
        const cards = Array.from(listContainer.querySelectorAll<HTMLElement>(':scope > [data-tasklist-uid]'));
        const viewTop = listContainer.scrollTop;
        let low = 0;
        let high = cards.length;
        while (low < high) {
            const middle = (low + high) >> 1;
            if (cards[middle].offsetTop + cards[middle].offsetHeight <= viewTop) {
                low = middle + 1;
            } else {
                high = middle;
            }
        }
        for (let index = low; index < cards.length; index++) {
            const uid = cards[index].dataset.tasklistUid;
            if (uid) {
                yield {uid, rect: cards[index].getBoundingClientRect()};
            }
        }
    }

    // ---------------------------------------------------------------- rendering helpers

    private _keys(): string[] {
        return (this._entries ?? []).map(entry => entry.key);
    }

    private _blockEntries(): TasklistEntry[] {
        if (!this._entries || !this._block) {
            return [];
        }
        const range = TasklistWindowHelper.resolve(this._keys(), this._block);

        return range === null ? [] : this._entries.slice(range.start, range.end + 1);
    }

    private _rowByKey(key: string): HTMLElement | null {
        return this._listContainer?.querySelector<HTMLElement>(`:scope > [data-tasklist-key="${CSS.escape(key)}"]`) ?? null;
    }

    /**
     * Shows a hint instead of the usual empty message when an activity filter is set and the tenant
     * has no audit trail at all — otherwise an empty list would read as a defect. Probes only in the
     * already-empty case, with a single existence check.
     */
    private async _resolveNoResultsMessage(): Promise<string> {
        if (FilterHelper.isActivityFilterActive(appState.filters) && !(await container.resolve(AuditLogRepository).hasAnyAuditLog())) {
            return get(`tasklist.no-audit-trail`);
        }

        return get(`tasklist.no-results`);
    }

    protected render(): unknown {
        const cssClassesIssueContainer = {
            'ha-tasklist__issue-container': true,
            [this._view]: true
        };
        const rows = this._blockEntries();
        const columnView = this._view === 'column-view';

        return html`
            <div class="view__body">
                <div class="view__column">
                    ${ToolbarHelper.renderTimelineToolbar(this._toolbarItems)}
                    <div class="ha-tasklist__search-and-list">
                        <div class="grid-view-helper-boxes">
                            <div class="grid-view-helper-box1"></div>
                            <div class="grid-view-helper-box2"></div>
                            <div class="grid-view-helper-box3"></div>
                        </div>
                        <div class=${classMap(cssClassesIssueContainer)}><div class="ha-tasklist__edge ha-tasklist__edge--top"></div>${repeat(
                                rows,
                                entry => entry.key,
                                (entry, index) => this._renderEntry(entry, columnView ? index + 2 : null)
                        )}<div class="ha-tasklist__edge ha-tasklist__edge--bottom"></div></div>
                    </div>
                </div>
                <div class="sidebar__filter ${appState.todoFilterOpen ? 'expanded' : 'collapsed'}">
                    <main class="sidebar__filter__main">
                        <ha-form-issue-filters class="wrapper"
                                                .issueTypeItemFilter=${buildTasklistIssueTypeItemFilter(this.mode)}
                        ></ha-form-issue-filters>
                    </main>
                </div>
            </div>
        `;
    }

    /**
     * One row. In column view every row gets its own grid row (`gridRow`, from its position in the
     * block), so cards of different status columns never share a row — as before, when the virtualizer
     * positioned every card itself.
     */
    private _renderEntry(entry: TasklistEntry, gridRow: number | null): TemplateResult {
        const rowStyle = gridRow === null ? nothing : `grid-row: ${gridRow}`;
        switch (entry.kind) {
            case 'issue':
                return html`
                    <ha-task-list-item
                            data-tasklist-key=${entry.key}
                            data-tasklist-uid=${entry.item.uid}
                            style=${rowStyle}
                            .isFirstOfFuture=${entry.item.uid === this._firstIssueUidInFuture}
                            .item=${entry.item}
                    ></ha-task-list-item>`;
            case 'today':
                return html`<hr class="today-begins-here" data-tasklist-key=${entry.key} style=${rowStyle}>`;
            case 'future':
                return html`<hr class="future-begins-here" data-tasklist-key=${entry.key} style=${rowStyle}>`;
            case 'notice':
                return html`<p class="ha-tasklist__no-results" data-tasklist-key=${entry.key} style=${rowStyle}>${entry.message}</p>`;
        }
    }
}
